"use client";

import { useEffect, useLayoutEffect, useState, useRef } from "react";

import { strings } from "@perceptron/constants";
import { cn } from "@perceptron/utils/cn";

import { SplitHandle, splitPaneVars } from "@demo/components/ResizableSplit";
import { TermsAndPrivacyDisclaimer } from "@demo/components/TermsAndPrivacyDisclaimer";
import { DEFAULT_COLUMN_WIDTH, PANEL_DEFAULTS, PANEL_MINIMUMS } from "@demo/libs/panel-layout";
import { useMediaCarouselStore } from "@demo/store/media-carousel";
import { AgentState, useModelStore } from "@demo/store/model";

import { ActiveTabProvider, useActiveTab } from "./ActiveTabContext";
import { COLUMN_GAP, defaultColumnFraction } from "./columnDefaults";
import ComposeInput from "./ComposeInput";
import ComposeOutput from "./ComposeOutput";
import MobileTabs from "./MobileTabs";

const ComposeLayout = () => {
  const { activeTab } = useActiveTab();
  const columnSplitRef = useRef<HTMLDivElement>(null);
  // `null` until the reader moves the divider: the prompt column is then `DEFAULT_COLUMN_WIDTH`, which CSS
  // clamps to the panes' minimums, so the server's markup is already the layout the client shows.
  const [columnFraction, setColumnFraction] = useState<number | null>(null);
  // That default as a share of the row, for the handle (its position for assistive tech and the arrow keys).
  const [defaultFraction, setDefaultFraction] = useState<number>(PANEL_DEFAULTS.horizontal);
  useLayoutEffect(() => {
    const width = columnSplitRef.current?.getBoundingClientRect().width ?? 0;
    if (width > 0) setDefaultFraction(defaultColumnFraction(width));
  }, []);
  const handleFraction = columnFraction ?? defaultFraction;

  return (
    <>
      <div
        // Clipped only where the panes slide past each other as tabs (phones); side by side, the clip would cut
        // off the prompt editor's drop shadow at the row's edge.
        className={cn(
          "group/tabs relative z-10 w-full flex-1 gap-4 overflow-hidden",
          "min-h-0 sm:flex sm:overflow-visible",
        )}
        data-active-tab={activeTab}
        ref={columnSplitRef}
        style={splitPaneVars(
          handleFraction,
          PANEL_MINIMUMS.promptPane,
          PANEL_MINIMUMS.outputPane,
          COLUMN_GAP,
          columnFraction === null ? DEFAULT_COLUMN_WIDTH : undefined,
        )}
      >
        <div
          className={cn(
            "absolute inset-0 h-full w-full transition-[transform,opacity] duration-300 ease-in-out",
            "group-data-[active-tab=input]/tabs:translate-x-0 group-data-[active-tab=input]/tabs:opacity-100",
            "group-data-[active-tab=output]/tabs:-translate-x-full group-data-[active-tab=output]/tabs:opacity-0",
            "sm:group-data-[active-tab=input]/tabs:relative sm:group-data-[active-tab=input]/tabs:translate-x-0 sm:group-data-[active-tab=input]/tabs:opacity-100",
            "sm:group-data-[active-tab=output]/tabs:relative sm:group-data-[active-tab=output]/tabs:translate-x-0 sm:group-data-[active-tab=output]/tabs:opacity-100",
            "sm:min-w-0",
            "split-pane-horizontal",
          )}
        >
          <ComposeInput />
        </div>
        <SplitHandle
          containerRef={columnSplitRef}
          fraction={handleFraction}
          gap={COLUMN_GAP}
          label={strings.compose.resizeColumns}
          minFirst={PANEL_MINIMUMS.promptPane}
          minSecond={PANEL_MINIMUMS.outputPane}
          orientation="horizontal"
          onFractionChange={setColumnFraction}
        />
        <div
          className={cn(
            "absolute inset-0 h-full w-full transition-[transform,opacity] duration-300 ease-in-out",
            "group-data-[active-tab=output]/tabs:translate-x-0 group-data-[active-tab=output]/tabs:opacity-100",
            "group-data-[active-tab=input]/tabs:translate-x-full group-data-[active-tab=input]/tabs:opacity-0",
            "sm:group-data-[active-tab=output]/tabs:relative sm:group-data-[active-tab=output]/tabs:translate-x-0 sm:group-data-[active-tab=output]/tabs:opacity-100",
            "sm:group-data-[active-tab=input]/tabs:relative sm:group-data-[active-tab=input]/tabs:translate-x-0 sm:group-data-[active-tab=input]/tabs:opacity-100",
            "sm:min-w-0 sm:flex-1",
          )}
        >
          <ComposeOutput />
        </div>
      </div>
      <MobileTabs />
      <TermsAndPrivacyDisclaimer className="mt-2 sm:hidden" />
    </>
  );
};

const ComposeSurface = () => {
  useEffect(() => {
    return () => {
      useModelStore.getState().stopStreaming();
      useModelStore.getState().clearAgentOutputAndSetAgentState(AgentState.IDLE);
      useMediaCarouselStore.getState().clearMediaInAgentCarousel();
    };
  }, []);

  return (
    <ActiveTabProvider>
      <ComposeLayout />
    </ActiveTabProvider>
  );
};

export default ComposeSurface;
